import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "src/app/auth/services/auth.service";
import { PasswordService } from "src/app/auth/services/password.service";
import { ApiService } from "src/app/core/api.service";
import { SyncCryptService } from "src/app/core/crypt/sync-crypt.service";
import { LoggerService } from "src/app/core/logger.service";
import { ErrorMessages } from "src/app/shared/models";
import { ResetpasswordpubkeyApiOutput } from "src/app/shared/models/api/resetpasswordpubkey-api.model";
import { passwordStrength } from 'check-password-strength'
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";

interface Fields {
  resetCode: string;
  newPass1: string;
  newPass2: string;
}

type Field = keyof Fields;

type FormControls<T> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

@Component({
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
})
export class ResetPasswordComponent implements OnInit {
  protected twofacode: string = '';
  protected errors: any[] = [];
  protected req2FA: boolean = false;
  private bCrypt: bCrypt;
  private success;
  protected form: FormGroup<FormControls<Fields>>;
  protected spinner: boolean = false;

  constructor(
    private pwdService: PasswordService,
    private loggerService: LoggerService,
    private auth: AuthService,
    private crypt: SyncCryptService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
  ) {
    this.bCrypt = new bCrypt();
  }

  ngOnInit() {
    this.form = this.fb.group({
      resetCode: ['', Validators.required],
      newPass1: ['', Validators.required],
      newPass2: ['', Validators.required],
    });
  }

  protected get page(): 'enter-password' | '2fa' {
    if (this.req2FA) {
      return '2fa';
    }
    else {
      return 'enter-password';
    }
  }

  protected get cachekey() {
    return this.route.snapshot.params.cachekey;
  }

  protected submit = async () => {
    this.errors = [];
    if (!this.pwdService.isPasswordStrong(this.newPass1)) {
        this.errors.push({ code : 8304});
        this.newPass1 = undefined;
        this.newPass2 = undefined;
        return;
    }
    if (this.newPass1 != this.newPass2) {
        this.errors.push({ code: 8121 });
        this.newPass1 = undefined;
        this.newPass2 = undefined;
        return;
    }
    this.errors = [];

    try {
      this.spinner = true;
      await this.resetPassword(
        this.resetCode,
        this.cachekey,
        this.newPass1,
        this.twofacode
      );
      this.success = true;
      this.resetCode = undefined;
      this.newPass1 = undefined;
      this.newPass2 = undefined;
      this.router.navigate(['/login'], { queryParams : { passwordreset: 1 }});
    }
    catch (err) {
      this.spinner = false;

      // if an error occurred, show error
      if (err && err.error_code) {
          let msg = err.error_msg || '';
          if (err.error_code == 8022 || err.error_code == 8023) {
              this.req2FA = true;
              msg = ErrorMessages[err.error_code];
              return;
          }
          this.loggerService.error('An error occurred password reset');
          this.loggerService.error(msg);
          this.errors.push({ code: err.error_code, msg: msg });
      }
      else if (err && err.code) {
        if (err.code == 8022 || err.code == 8023) {
            this.req2FA = true;
            return;
        }
        this.errors.push(err);
      } else {
          this.loggerService.error('An error occurred');
          this.loggerService.error(err);
          this.errors.push({
              code: 9000,
              msg: 'An unknown error occurred',
          });
      }
    }
  }

  private async resetPassword(
      code: string,
      cachekey: string,
      passwordPlain: string,
      twofacode?: string
  ) {
      const hashPass = await this.auth.hashPassword(
          this.bCrypt.gensalt(8),
          passwordPlain
      );
      const pubkey = await this.apiService.fetchText(
          '/key/password-recover/pubkey'
      );
      const passResetKeys = await this.crypt.prepareResetPasswords(
          passwordPlain,
          pubkey
      );
      const pubkeyOut =
          await this.apiService.send<ResetpasswordpubkeyApiOutput>(
              'resetpasswordpubkey',
              {
                  cachekey,
                  code,
              }
          );
      const encPassword = await this.crypt.userpasswordEncrypt(
          passwordPlain,
          pubkeyOut.pub_key
      );

      return await this.apiService.execute('resetpassword', {
          enc_pass_meta: passResetKeys.encPassMeta,
          salt_meta: passResetKeys.metaSalt,
          enc_pass_priv: passResetKeys.encPassPriv,
          salt_priv: passResetKeys.privSalt,
          password_hash: hashPass,
          enc_password: encPassword,
          code: code,
          cachekey: cachekey,
          twofacode: twofacode,
      });
  }

  private isPasswordValid() {
    return this.checkPassword().length === 0;
  }

  protected get canSubmit() {
    return this.form.valid && this.isPasswordValid() && this.newPass1 === this.newPass2;
  }

  private checkPassword() {
    const errors = [];
    if (this.newPass1.length < 8 || this.newPass1.length > 72) {
      errors.push('length');
    }
    return errors;
  }

  protected requirementPassed(name: 'length') {
    return this.checkPassword().indexOf(name) === -1;
  }

  protected getErrorMessage(field: Field) {
    if (!this.form.controls[field].touched) return;

    if (this.form.controls[field].hasError('required')) {
      return 'This field is required';
    }

    if (field == 'newPass1') {
      if (!this.isPasswordValid()) {
        return 'Please enter a valid password';
      }
    }
    else if (field == 'newPass2') {
      if (this.newPass1 !== this.newPass2) {
        return 'Passwords do not match';
      }
    }
  }

  protected get passwordStrength() {
    return {
      color: 'red',
      label: passwordStrength(this.newPass1).value.toLowerCase(),
      value: passwordStrength(this.newPass1).id/3,
    }
  }

  protected get resetCode() {
    return this.form.controls.resetCode.value;
  }

  protected set resetCode(value: string) {
    this.form.controls.resetCode.setValue(value);
  }

  protected get newPass1() {
    return this.form.controls.newPass1.value;
  }

  protected set newPass1(value: string) {
    this.form.controls.newPass1.setValue(value);
  }

  protected get newPass2() {
    return this.form.controls.newPass2.value;
  }

  protected set newPass2(value: string) {
    this.form.controls.newPass2.setValue(value);
  }

  // UI handlers
  protected handleTwoFactor = (twofacode: string) => {
    this.twofacode = twofacode;
    this.submit();
  }
}
